import { Injectable } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { IOmgeving, IUser } from '../interfaces/user.interface';
import { TeamsService } from './teams.service';
import { RoleEnum } from '../enums/role.enum';
import { JwtUtil } from '../helpers/jwt.util';
import { IClub } from '../interfaces/club.interface';
import { ClubPermissionService } from './club-permission.service';
import { StorageUrlService } from './storage-url.service';

/** Aanvraag vanaf het inlogscherm (siq_auth AccessRequestData). */
export interface IToegangAanvraag {
  kind: 'club' | 'member';
  clubName: string;
  city?: string;
  country?: string;
  team?: string;
  role?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message?: string;
  language?: string;
  /** Lokveld; een mens laat het leeg. */
  website?: string;
}

/** Uitkomst van de eerste inlogstap. */
export interface ILoginResult {
  /** Is er nog een tweede stap nodig? */
  requiresTwoFactor: boolean;
  /** Kortlevend bewijs voor die tweede stap. */
  challenge?: string;
  /** Welke methoden deze gebruiker aan heeft staan: "authenticator", "email". */
  methods?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  avatarUpdated$ = new Subject<string>();

  public set token(value: string) {
    this.localStorage.store('token', value);
  }

  public get token(): string {
    return this.localStorage.retrieve('token') ?? null;
  }

  public set user(value: IUser) {
    this.localStorage.store('user', value);
  }

  public get user(): IUser {
    return this.localStorage.retrieve('user') ?? null;
  }

  constructor(private localStorage: LocalStorageService,
              private teamsService: TeamsService,
              private httpClient: HttpClient,
              private clubPermissionService: ClubPermissionService,
              private storageUrlService: StorageUrlService) {
  }

  /** Vraagt een herstel-link aan. Het antwoord is altijd hetzelfde, of het
   *  adres nu bekend is of niet. */
  public forgotPassword(email: string): Observable<unknown> {
    return this.httpClient.post(`${environment.authUrl}/ForgotPassword`, { email });
  }

  /**
   * "Register your club" of "Request an account at your club" vanaf het
   * inlogscherm: gaat als mail naar ons. De pagina geeft een context mee die
   * de algemene foutmelding uitzet; zij toont haar eigen.
   */
  public requestAccess(aanvraag: IToegangAanvraag, context?: HttpContext): Observable<unknown> {
    return this.httpClient.post(`${environment.authUrl}/RequestAccess`, aanvraag, { context });
  }

  /** Zet een nieuw wachtwoord met de token uit de e-mail. */
  public restorePassword(token: string, password: string, naam?: { firstName: string; lastName: string }): Observable<unknown> {
    // De naam alleen bij een uitnodiging met alleen een e-mailadres
    // (ledenlijst); auth zet hem alleen als het account er nog geen heeft.
    return this.httpClient.post(`${environment.authUrl}/RestorePassword/${token}`, { password, ...(naam ?? {}) });
  }

  /**
   * Inloggen met e-mailadres en wachtwoord.
   *
   * Heeft de gebruiker een tweede stap aanstaan, dan komt er geen token maar
   * een kortlevend bewijs terug; het scherm vraagt dan om een code en rondt af
   * met {@link loginTwoFactor}.
   */
  public login(data: {email: string, password: string}): Observable<ILoginResult> {
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Login`, data)
      .pipe(
        map((res) => {
          if (res?.requiresTwoFactor) {
            return {
              requiresTwoFactor: true,
              challenge: res.challenge,
              methods: res.twoFactorMethods ?? []
            } as ILoginResult;
          }
          this.bewaarSessie(res.token);
          return { requiresTwoFactor: false } as ILoginResult;
        }),
        // Check club permission after login (een super-admin heeft nog geen club)
        tap((res) => { if (!res.requiresTwoFactor && !this.isBeheerderZonderClub) this.clubPermissionService.checkPermission().subscribe(); })
      );
  }

  /** De tweede stap: code uit de app, uit de e-mail, of een herstelcode. */
  public loginTwoFactor(challenge: string, code: string): Observable<void> {
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Login/TwoFactor`, { challenge, code })
      .pipe(
        map((res) => { this.bewaarSessie(res.token); }),
        tap(() => { if (!this.isBeheerderZonderClub) this.clubPermissionService.checkPermission().subscribe(); })
      );
  }

  /** Vraagt om een code per e-mail, halverwege het inloggen. */
  public sendTwoFactorEmail(challenge: string): Observable<unknown> {
    return this.httpClient.post(`${environment.authUrl}/Account/Login/TwoFactor/SendEmail`, { challenge });
  }

  /** Wat er na een geslaagde inlog moet gebeuren; op één plek, omdat het nu
   *  langs twee wegen kan lopen. */
  private bewaarSessie(token: string): void {
    this.token = token;
    this.user = JwtUtil.parse(token);
    this.teamsService.teams = this.user.team ?? [];
    // Zonder dit bewijs blijven video's, logo's en avatars leeg tot de
    // volgende paginaherlaad — /storage/ eist het sinds 20-08-2026.
    this.storageUrlService.refresh();
  }

  /**
   * "Open als club" vanuit de beheerconsole: het token is daar al gemaakt
   * (auth /Account/Impersonate), hier alleen nog de sessie overnemen. Een
   * eventuele eerdere inlog in deze browser gaat eruit.
   */
  public loginMetToken(token: string): void {
    this.logout();
    this.bewaarSessie(token);
    this.clubPermissionService.checkPermission().subscribe();
  }

  /**
   * Het token vernieuwen met de rechten zoals die nu gelden. De rechtenlijst
   * zit in het token en kwam tot 12-09-2026 alleen bij het inloggen mee;
   * een nieuw recht zag je pas na opnieuw inloggen. Geeft terug of de
   * rechten veranderd zijn, zodat de aanroeper het scherm kan verversen.
   */
  public vernieuwToken(): Observable<boolean> {
    const oud = JSON.stringify(this.user?.action ?? []);
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Refresh`, {}).pipe(
      map((res) => {
        if (!res?.token) { return false; }
        this.bewaarSessie(res.token);
        return JSON.stringify(this.user?.action ?? []) !== oud;
      })
    );
  }

  /**
   * De omgevingen waarin deze gebruiker kan werken: de eigen club, plus de
   * league-omgevingen waar een van zijn teams in speelt
   * (docs/QFA-LEAGUE-OMGEVING.md). Eén item = niets te kiezen.
   */
  public omgevingen(): Observable<IOmgeving[]> {
    return this.httpClient.get<IOmgeving[]>(`${environment.authUrl}/Account/Environments`);
  }

  /** Kijkt deze sessie mee in een league-omgeving (alleen lezen)? */
  public get kijktInLeague(): boolean {
    return !!this.user?.home_club && this.user?.role !== 'league_editor';
  }

  /**
   * Mag deze sessie alleen kijken? Meekijken in een league (league_viewer) of
   * de kijkrol als account (club_viewer). De gateway laat dan alleen lezen
   * door en downloaden niet; knoppen voor reageren, downloaden en exporteren
   * horen dan weg.
   */
  public get alleenKijken(): boolean {
    return this.user?.role === 'league_viewer' || this.user?.role === 'club_viewer';
  }

  /**
   * Een super-admin met zijn eigen inlog, nog zonder club. Tot 24-10-2026
   * werd die hier geweigerd ("gebruik Open als club in de beheerconsole");
   * nu kiest hij eerst een club (/auth/kies-club).
   */
  public get isBeheerderZonderClub(): boolean {
    const rol = typeof this.user?.role === 'string' ? this.user.role : this.user?.role?.key;
    return rol === RoleEnum.SUPER_ADMIN && !this.user?.impersonated_by;
  }

  /**
   * Een super-admin die als deze gebruiker meekijkt ("Open as user" in de
   * beheerconsole, 25-10-2026). Het token is dat van de gebruiker, dus de app
   * ziet eruit zoals hij hem ziet; alleen kijken (meekijken.interceptor.ts).
   */
  public get meekijken(): boolean {
    return !!this.user?.viewed_by;
  }

  /** Een club die een super-admin opende (clubkiezer, of "Open als club" in de beheerconsole). */
  public get isBeheerderInClub(): boolean {
    return !!this.user?.impersonated_by;
  }

  /** Draagt deze sessie vanuit de eigen club bij aan een league-omgeving? */
  public get draagtBijAanLeague(): boolean {
    return !!this.user?.home_club && this.user?.role === 'league_editor';
  }

  /**
   * Naar een andere omgeving: de eigen club, of meekijken in een league.
   * De server geeft een nieuw token; daarna de app opnieuw laden. Dat is
   * bewust: de rechten, de gekozen ploegen, de socket en het opslagbewijs
   * horen allemaal bij de omgeving, en een herlaad zet ze in één keer goed.
   */
  public wisselOmgeving(club: string): Observable<void> {
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Environment`, { club }).pipe(
      map((res) => {
        this.localStorage.clear('teams');
        this.bewaarSessie(res.token);
        window.location.assign('/');
      })
    );
  }

  public logout(): void {
    this.localStorage.clear('token');
    this.localStorage.clear('user');
    this.localStorage.clear('teams');
    this.storageUrlService.clear();
  }

  public saveUser(data: IUser): Observable<IUser>  {
    return this.httpClient.patch<IUser>(`${environment.authUrl}/Account`, data).pipe(
      map((res: any) => {
        this.token = res.token;
        this.user = JwtUtil.parse(res.token);

        return res;
      })
    );
  }

  public uploadAvatarUser(file: File): Observable<any> {
    const data = new FormData();
    data.append('avatar', file);

    return this.httpClient.post<IAvatarResponse>(`${environment.authUrl}/Account/Avatar`, data, {
      observe: 'body'
    }).pipe(
      tap((response) => this.avatarUpdated$.next(response.avatar)),
      tap((response) => this.token = response.token)
    );
  }

  public changePassword(password: string): Observable<string> {

    return this.httpClient.patch<IUser>(`${environment.authUrl}/Account`, {password}).pipe(
      map((res: any) => {
        this.token = res.token;
        this.user = JwtUtil.parse(res.token);

        return res;
      })
    );
  }

  public getAccount(): Observable<IUser> {
    return this.httpClient.get<IUser>(`${environment.authUrl}/Account/User`);
  }
}

interface IAvatarResponse {
  sid: string;
  email: string;
  firstName: string;
  lastName: string;
  languageKey: string;
  clubId: number;
  club: IClub;
  teams: string[];
  phone: string;
  role: string;
  deleted: boolean;
  avatar: string;
  token: string;
}
